import type { QueryKey } from '@tanstack/react-query';
import { logClientError } from '@/lib/errorLogger';

/**
 * The global catch for every failed query and mutation (wired to the
 * QueryCache and MutationCache in App.tsx).
 *
 * Until 2026-11-03 this sent the message, the first word of the query key and
 * the HTTP status, and dropped everything else: no stack trace, so the Errors
 * panel could say that a request failed but never which file asked for it
 * (Drew, on Andrew's "AbortError: The operation was aborted" with no stack:
 * "every bug I want to know what it was, where it happened, when, why, and how
 * to fix it"). The full report now carries the stack and the whole key.
 */
export function reportQueryFailure(
  error: unknown,
  kind: 'query' | 'rpc',
  key?: QueryKey,
): void {
  const e = error as { message?: string; status?: number; stack?: string; name?: string } | undefined;
  const msg = e?.message ?? String(error ?? 'unknown error');
  // SessionExpiredError is already handled cleanly by the auth layer — don't double-log it.
  if (/session_expired/i.test(msg)) return;
  const name = key && key.length > 0 ? String(key[0]) : null;
  void logClientError({
    error_message: `${kind}: ${msg}`,
    stack_trace: typeof e?.stack === 'string' && e.stack.length > 0 ? e.stack : null,
    query_name: name,
    http_status: typeof e?.status === 'number' ? e.status : null,
    // Keys hold ids and view names, never client data; the whole key says which
    // org or user the list was for, which the first word alone does not.
    input_data: key ? { queryKey: key, errorName: e?.name ?? null } : { errorName: e?.name ?? null },
    error_type: kind,
  });
}
